import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCopy, faCheck } from "@fortawesome/free-solid-svg-icons"

import { notify } from "shared/ui/Notifications/notify"
import { highlightDataq } from "features/Docs/utils/highlightDataq"
import { highlightQuery } from "features/Docs/utils/highlightQuery"
import { Token } from "features/Docs/types/docs.types"

interface DocsCodeBlockProps {
  code: string
  language?: string
}

const LANGUAGE_LABELS: Record<string, string> = {
  dataq: "DATA(Q)",
  query: "Query",
}

const tokenize = (code: string, language?: string): Token[][] | null => {
  if (language === "dataq") return highlightDataq(code)
  if (language === "query") return highlightQuery(code)
  return null
}

/**
 * A fenced code block for docs. `dataq` and `query` snippets are statically
 * highlighted (light editor panel / dark query panel respectively) with a
 * language chip; any other language renders as plain monospace. Every block
 * gets a copy button.
 */
const DocsCodeBlock: React.FC<DocsCodeBlockProps> = ({ code, language }) => {
  const [copied, setCopied] = useState(false)
  const clean = code.replace(/\n$/, "")
  const lines = useMemo(() => tokenize(clean, language), [clean, language])
  const label = language ? LANGUAGE_LABELS[language] : undefined
  const variant = language === "query" ? "query" : "dataq"

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(clean)
      setCopied(true)
      notify.toast.success("Copied to clipboard")
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      notify.toast.error("Couldn't copy to clipboard")
    }
  }

  return (
    <div className={`docs-code docs-code--${variant}`} data-language={language}>
      <div className="docs-code__header">
        {label && <span className="docs-code__lang">{label}</span>}
        <button
          type="button"
          className="docs-code__copy"
          onClick={handleCopy}
          aria-label="Copy code to clipboard"
        >
          <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      <pre className="docs-code__body">
        <code>
          {lines
            ? lines.map((tokens, lineIndex) => (
                <React.Fragment key={lineIndex}>
                  {tokens.map((token, tokenIndex) =>
                    token.className ? (
                      <span key={tokenIndex} className={token.className}>
                        {token.text}
                      </span>
                    ) : (
                      <React.Fragment key={tokenIndex}>
                        {token.text}
                      </React.Fragment>
                    ),
                  )}
                  {lineIndex < lines.length - 1 ? "\n" : ""}
                </React.Fragment>
              ))
            : clean}
        </code>
      </pre>
    </div>
  )
}

export default DocsCodeBlock
